/**
 * Presentation Editor Page
 * 
 * MVP: Simple JSON editor for JSON Schema and UI Schema.
 * Future: Drag-and-drop visual editor.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';


interface PresentationEditorProps {
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
}

export const PresentationEditor: React.FC<PresentationEditorProps> = ({ tenantId, apiBaseUrl, authToken }) => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  
  const [workflow, setWorkflow] = useState<any>(null);
  const [jsonSchema, setJsonSchema] = useState('');
  const [uiSchema, setUiSchema] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWorkflow();
  }, [workflowId]);

  const fetchWorkflow = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/workflows/${workflowId}?tenantId=${encodeURIComponent(tenantId)}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow: ${response.statusText}`);
      }

      const data = await response.json();
      setWorkflow(data);
      setJsonSchema(JSON.stringify(data.package.schemas.jsonSchema, null, 2));
      setUiSchema(JSON.stringify(data.package.schemas.uiSchema, null, 2));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Validate JSON
      const parsedJsonSchema = JSON.parse(jsonSchema);
      const parsedUiSchema = JSON.parse(uiSchema);

      const response = await fetch(
        `${apiBaseUrl}/api/workflows/${workflowId}/presentation?tenantId=${encodeURIComponent(tenantId)}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            jsonSchema: parsedJsonSchema,
            uiSchema: parsedUiSchema
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to save: ${response.statusText}`);
      }

      alert('Presentation updated successfully!');
      navigate(`/workflows/${workflowId}`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/workflows/${workflowId}`);
  };

  const secBtn = 'px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50';
  const priBtn = 'px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50';
  const monoCls = 'w-full flex-1 px-3 py-3 border border-gray-300 rounded-lg text-body font-mono text-meta bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast resize-none';

  if (loading) return (
    <div className="min-h-screen bg-surface-app flex items-center justify-center">
      <p className="text-body text-gray-400">Loading…</p>
    </div>
  );

  if (error && !workflow) return (
    <div className="min-h-screen bg-surface-app flex flex-col items-center justify-center gap-4">
      <p className="text-body text-error">Error: {error}</p>
      <button type="button" onClick={() => navigate('/workflows')} className={secBtn}>Back to List</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-app flex flex-col">
      {/* Header */}
      <div className="bg-surface-panel border-b border-gray-200 px-8 py-4 flex-shrink-0">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate('/')} className={secBtn}>← Studio</button>
            <h1 className="text-2xl font-semibold text-gray-900">Edit Presentation: {workflow?.metadata.displayName}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={handleCancel} disabled={saving} className={secBtn}>Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className={priBtn}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-screen-xl mx-auto w-full px-8 mt-4">
          <div className="px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {/* Panels */}
      <div className="flex-1 px-8 py-6 max-w-screen-xl mx-auto w-full grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-2">
          <h2 className="text-body font-semibold text-gray-900">JSON Schema</h2>
          <p className="text-label text-gray-500">Defines the structure and validation rules for workflow parameters.</p>
          <textarea value={jsonSchema} onChange={e => setJsonSchema(e.target.value)} spellCheck={false} aria-label="JSON Schema" className={monoCls} />
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="text-body font-semibold text-gray-900">UI Schema</h2>
          <p className="text-label text-gray-500">Defines the presentation and ordering of parameters in the UI.</p>
          <textarea value={uiSchema} onChange={e => setUiSchema(e.target.value)} spellCheck={false} aria-label="UI Schema" className={monoCls} />
        </div>
      </div>

      <div className="px-8 py-4 border-t border-gray-200 max-w-screen-xl mx-auto w-full">
        <p className="text-label text-gray-400">
          <strong>Note:</strong> This is a simplified JSON editor. Future versions will include a visual drag-and-drop interface.
        </p>
      </div>
    </div>
  );
};

